import type { ClientMessage, ServerMessage } from '../../shared/protocol.ts';
import { getToken } from './api.ts';
import { readGuest, saveGuestCredentials } from './guest.ts';
import type { AvatarConfig } from '../types';

/**
 * Canal de tiempo real.
 *
 * Reconecta solo, con espera creciente, y vuelve a entrar a la sala en la que
 * estabas. Perder el wifi un segundo a mitad de partida no debería costarte
 * la partida: el servidor conserva tu puntaje mientras reapareces.
 */

type Listener = (msg: ServerMessage) => void;
type StatusListener = (status: ConnectionStatus) => void;

export type ConnectionStatus = 'connecting' | 'online' | 'reconnecting' | 'offline';

const MAX_BACKOFF_MS = 15_000;

export class Realtime {
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private statusListeners = new Set<StatusListener>();
  private queue: ClientMessage[] = [];
  private attempt = 0;
  private reconnectTimer: number | null = null;
  private pingTimer: number | null = null;
  private closedByUs = false;

  /** PIN de la sala actual, para volver a entrar tras reconectar. */
  private currentPin: string | null = null;
  private guestName: string | undefined;
  private guestAvatar: AvatarConfig | undefined;

  /** Desfase entre el reloj del servidor y el del navegador, en ms. */
  clockOffset = 0;
  status: ConnectionStatus = 'offline';

  connect(guest?: { name: string; avatar: AvatarConfig }): void {
    // Se recuerdan para las reconexiones: al volver de una caída el servidor
    // vuelve a pedir `auth`, y un invitado sin estos datos reaparecería en la
    // sala con otro nombre y otra cara.
    if (guest !== undefined) {
      this.guestName = guest.name;
      this.guestAvatar = guest.avatar;
    }
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    this.closedByUs = false;
    this.setStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');

    const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${scheme}://${location.host}/ws`);
    this.ws = ws;

    ws.onopen = () => {
      this.attempt = 0;
      // El primer mensaje siempre es `auth`: el servidor no acepta nada antes.
      // La identidad de invitado va en cada `auth`: es lo que permite volver
      // al MISMO jugador tras una caída en vez de aparecer como otro.
      const invitado = readGuest();
      ws.send(
        JSON.stringify({
          t: 'auth',
          token: getToken(),
          guestName: this.guestName,
          guestAvatar: this.guestAvatar,
          guestId: invitado?.id,
          guestToken: invitado?.token,
        }),
      );

      // Reentrar a la sala tras una caída.
      if (this.currentPin) {
        ws.send(JSON.stringify({ t: 'room:join', pin: this.currentPin }));
      }
      for (const msg of this.queue.splice(0)) ws.send(JSON.stringify(msg));

      this.setStatus('online');
      this.startPing();
    };

    ws.onmessage = (event) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(event.data as string) as ServerMessage;
      } catch {
        return;
      }

      if (msg.t === 'pong') {
        this.clockOffset = msg.serverNow - Date.now();
        return;
      }
      // El servidor entrega el testigo una sola vez, al crear la identidad.
      if (msg.t === 'hello' && msg.isGuest && msg.guestToken) {
        saveGuestCredentials(msg.userId, msg.guestToken);
      }
      if (msg.t === 'room:state') this.currentPin = msg.room.pin;
      if (msg.t === 'room:closed' || msg.t === 'kicked') this.currentPin = null;

      for (const listener of this.listeners) listener(msg);
    };

    ws.onclose = () => {
      this.stopPing();
      this.ws = null;
      if (this.closedByUs) {
        this.setStatus('offline');
        return;
      }
      this.scheduleReconnect();
    };

    ws.onerror = () => ws.close();
  }

  private scheduleReconnect(): void {
    this.setStatus('reconnecting');
    // Espera creciente con algo de azar: si se cae el servidor, cien clientes
    // no vuelven todos en el mismo milisegundo.
    const base = Math.min(MAX_BACKOFF_MS, 500 * 2 ** this.attempt);
    const delay = base * (0.7 + Math.random() * 0.6);
    this.attempt += 1;

    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = window.setTimeout(() => this.connect(), delay);
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = window.setInterval(() => this.send({ t: 'ping' }), 20_000);
  }

  private stopPing(): void {
    if (this.pingTimer) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private setStatus(status: ConnectionStatus): void {
    if (this.status === status) return;
    this.status = status;
    for (const listener of this.statusListeners) listener(status);
  }

  send(msg: ClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    } else {
      // Guardamos para enviar al reconectar, salvo los pings, que caducan.
      if (msg.t !== 'ping') this.queue.push(msg);
      this.connect();
    }
  }

  on(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onStatus(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    listener(this.status);
    return () => this.statusListeners.delete(listener);
  }

  /** Hora del servidor estimada: es la que manda para los cronómetros. */
  serverNow(): number {
    return Date.now() + this.clockOffset;
  }

  leaveRoom(): void {
    this.currentPin = null;
    this.send({ t: 'room:leave' });
  }

  disconnect(): void {
    this.closedByUs = true;
    this.currentPin = null;
    this.queue = [];
    this.attempt = 0;
    this.stopPing();
    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);

    const ws = this.ws;
    // Soltar la referencia ANTES de cerrar. `close()` es asíncrono: si la
    // dejáramos puesta, un `connect()` inmediato —el que ocurre al iniciar
    // sesión— vería el socket todavía en OPEN, se daría por conectado y
    // saldría sin abrir el nuevo.
    this.ws = null;
    ws?.close();
  }
}

export const realtime = new Realtime();
